import React from 'react';

import { useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { TranslateFunction } from 'react-localize-redux';

import { Currency } from '../../../../../core/api/general/general.types';
import { MenuSection } from '../../../../../core/api/menu/menu.types';
import { analyticsService } from '../../../../../core/services/analyticsService';
import {
  AddItemHookPayload,
  BasketSource,
  RemoveItemHookPayload,
} from '../../../../../core/types/basket.types';
import { ClickItemPayloadType } from '../../../../../core/types/menu.types';
import { useMenuSearch } from '../../../../../pages/RestaurantMenu/hooks/useMenuSearch';
import { Item as BasketItem } from '../../../../../weborder/BasketControl';
import { IconButton } from '../../../../atoms/IconButton';
import { TextField } from '../../../../atoms/TextField';
import { ArrowLeft } from '../../../../icons/ArrowLeft';
import { InfoFaceIcon } from '../../../../icons/InfoFaceIcon';
import { Search } from '../../../../icons/Search';
import { X as XIcon } from '../../../../icons/X';
import { Fallback } from '../../../../layouts/Fallback';
import { MenuItemsList } from '../../../../organisms/MenuItemsList';

const useStyles = (showTopBorder) =>
  makeStyles((theme: Theme) => ({
    menuItemsList: {
      margin: theme.spacing(0, 2),
    },
    menuSearchIcon: {
      color: theme.palette.common.black,
    },
    textField: {
      height: 48,
      paddingBottom: 0,
    },
    textFieldContainer: {
      height: 41,
    },
    textFieldMobile: {
      height: 56,
      width: '100%',
      paddingBottom: 0,

      '& .MuiInputBase-root': {
        backgroundColor: theme.palette.common.white,
        borderBottom: 'none',
        height: '100%',
      },
    },
    root: {
      borderTop: showTopBorder
        ? `1px solid ${theme.palette.grey[400]}`
        : 'none',
      paddingTop: theme.spacing(1),
      margin: theme.spacing(0, 2),

      '& .MuiInputAdornment-root': {
        marginRight: 0,
      },
    },
    [theme.breakpoints.up('sm')]: {
      menuItemsList: {
        margin: 0,
      },
      root: {
        margin: theme.spacing(0, 3),
      },
    },
    [theme.breakpoints.up('md')]: {
      root: {
        margin: 0,
      },
    },
    [theme.breakpoints.up('lg')]: {
      textFieldContainer: {
        height: 54,
      },
    },
  }));

type Props = {
  basketItems: BasketItem[];
  closeMenuSearch: () => void;
  currency: Currency;
  languageCode: string;
  menuSections: MenuSection[];
  onAddItem: (payload: AddItemHookPayload) => void;
  onItemClick: (paypal: ClickItemPayloadType) => void;
  onRemoveItem: (payload: RemoveItemHookPayload) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  showTopBorder?: boolean;
  translate: TranslateFunction;
};

export const MenuSearch = (props: Props) => {
  const {
    basketItems,
    closeMenuSearch,
    currency,
    languageCode,
    menuSections,
    onAddItem,
    onItemClick,
    onRemoveItem,
    searchValue,
    setSearchValue,
    showTopBorder = true,
    translate,
  } = props;
  const classes = useStyles(showTopBorder)();

  const theme = useTheme();
  const isDesktopScreen = useMediaQuery(theme?.breakpoints.up('sm'));

  const {
    data: menuItemsData,
    isDataEmpty: isMenuItemsDataEmpty,
    isSearchValueEmpty,
  } = useMenuSearch(menuSections, searchValue);

  const showDesktopEmptyListFallback =
    isDesktopScreen && !isSearchValueEmpty && isMenuItemsDataEmpty;

  const showMobileEmptyListFallback =
    !isDesktopScreen && (isMenuItemsDataEmpty || isSearchValueEmpty);

  const showFallback =
    showDesktopEmptyListFallback || showMobileEmptyListFallback;

  const showList = !showFallback && !isSearchValueEmpty;

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Search',
    };
    analyticsService.track(eventData);
  };

  const handleOnClickCloseButton = (): void => {
    if (isDesktopScreen) {
      trackBackSelected();
      closeMenuSearch();
    }
    setSearchValue('');
  };

  const handleOnClickBackButton = (): void => {
    setSearchValue('');
    trackBackSelected();
    closeMenuSearch();
  };

  const handleOnBlurTextField = (): void => {
    const eventData = {
      amp_label: 'Menu search used',
      amp_numberOfResults: menuItemsData.length,
      amp_searchTerm: searchValue,
    };
    analyticsService.track(eventData);
  };

  const backButton = (
    <IconButton
      color="secondary"
      data-fd="menu-search-back-button"
      onClick={handleOnClickBackButton}
    >
      <ArrowLeft />
    </IconButton>
  );

  const closeIcon = (
    <IconButton
      color="secondary"
      data-fd="menu-search-close-button"
      disableRipple
      onClick={handleOnClickCloseButton}
    >
      <XIcon />
    </IconButton>
  );

  const renderCloseIcon = (): JSX.Element => {
    if (isDesktopScreen) {
      return closeIcon;
    }

    if (!isSearchValueEmpty) {
      return closeIcon;
    }

    return null;
  };

  const renderStartIcon = isDesktopScreen ? (
    <Search className={classes.menuSearchIcon} />
  ) : (
    backButton
  );

  const renderFallbackIcon = (): JSX.Element => {
    const infoFaceIcon = <InfoFaceIcon data-fd="info-face-icon" />;

    if (isDesktopScreen) {
      return infoFaceIcon;
    }

    if (isSearchValueEmpty) {
      return <Search data-fd="search-icon" viewBox="0 0 24 24" />;
    }

    return infoFaceIcon;
  };

  return (
    <div className={isDesktopScreen ? classes.root : ''} data-fd="menu-search">
      <div className={isDesktopScreen ? classes.textFieldContainer : ''}>
        <TextField
          autoFocus
          className={
            isDesktopScreen ? classes.textField : classes.textFieldMobile
          }
          endIcon={renderCloseIcon()}
          inputProps={{ maxLength: 100 }}
          onBlur={handleOnBlurTextField}
          onChange={(e): void => setSearchValue(e.target.value)}
          placeholder={translate('MenuSearch_SearchMenu') as string}
          startIcon={renderStartIcon}
          value={searchValue}
          variant={isDesktopScreen ? 'outlined' : 'standard'}
        />
      </div>
      {showList && (
        <div className={classes.menuItemsList} data-fd="search-menu-items-list">
          <MenuItemsList
            basketItems={basketItems}
            currency={currency}
            hasGreyBackground={!isDesktopScreen}
            languageCode={languageCode}
            menuItems={menuItemsData}
            onAddItem={onAddItem}
            onClickItem={onItemClick}
            onRemoveItem={onRemoveItem}
            source={BasketSource.MenuSearch}
            translate={translate}
            variant={isDesktopScreen ? 'list' : 'compact'}
          />
        </div>
      )}
      {showFallback && (
        <Fallback
          dataFd="menu-search-fallback"
          description={translate('MenuSearch_SearchOurMenu') as string}
          icon={renderFallbackIcon()}
          isDarkIconBg={!isDesktopScreen}
          title={
            !isSearchValueEmpty
              ? (translate('MenuSearch_NoSearchResults') as string)
              : ''
          }
          variant="panel"
        />
      )}
    </div>
  );
};
